/**
 * Resolves the Tagalys merchandising segment tag for collection pages.
 * The tag comes from the fixed `merch` URL parameter — there is no theme
 * setting for it — and is persisted for the browser session so it survives
 * navigation. Every storage access is defensive — this must never break the PLP.
 */

export const SEGMENT_TAG_STORAGE_KEY = 'tagalys:segmentTag';

/** The one URL parameter carrying the segment tag, e.g. `?merch=sep_molly_smith_story`. */
export const SEGMENT_TAG_PARAM = 'merch';

const MAX_SEGMENT_TAG_LENGTH = 100;

/**
 * Reads the segment tag out of a query string.
 * @param {string} search - Query string, with or without the leading `?`.
 * @returns {string|null} Trimmed value capped at 100 characters, or null.
 */
export function getSegmentTagFromSearch(search) {
  if (typeof search !== 'string' || search.length === 0) return null;

  const value = new URLSearchParams(search).get(SEGMENT_TAG_PARAM);
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (trimmed.length === 0) return null;

  return trimmed.slice(0, MAX_SEGMENT_TAG_LENGTH);
}

/**
 * Resolves the segment tag from the URL, persisting it for the session.
 * A value in the URL always wins and replaces anything already stored.
 * Stored values are re-trimmed and re-capped on read, never trusted verbatim.
 * @param {{ search?: string, storage?: Storage|null }=} options - Resolution inputs.
 * @returns {string|null} The segment tag, or null when there is none.
 */
export function resolveSegmentTag(options) {
  const opts = options || {};
  const storage = opts.storage;
  const fromUrl = getSegmentTagFromSearch(opts.search);

  if (fromUrl !== null) {
    try {
      if (storage) storage.setItem(SEGMENT_TAG_STORAGE_KEY, fromUrl);
    } catch {
      // Storage unavailable (Safari private mode, cookies blocked) — URL value still applies.
    }
    return fromUrl;
  }

  try {
    if (!storage) return null;
    const stored = storage.getItem(SEGMENT_TAG_STORAGE_KEY);
    if (typeof stored !== 'string' || stored.length === 0) return null;

    // Anything already in storage is re-normalised — it may predate the current
    // rules or have been written by hand in DevTools.
    const normalised = stored.trim().slice(0, MAX_SEGMENT_TAG_LENGTH);
    return normalised.length > 0 ? normalised : null;
  } catch {
    return null;
  }
}

/**
 * Convenience wrapper reading the current page and sessionStorage.
 * @returns {string|null} The segment tag for this page view, or null.
 */
export function getSegmentTag() {
  if (typeof window === 'undefined') return null;

  const search = window.location && typeof window.location.search === 'string' ? window.location.search : '';

  let storage = null;
  try {
    storage = window.sessionStorage || null;
  } catch {
    // Accessing sessionStorage itself can throw when storage is disabled.
    storage = null;
  }

  return resolveSegmentTag({ search, storage });
}
